import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private promptEvent: any = null;
  private promptAvailable$ = new BehaviorSubject<boolean>(false);
  private readonly DISMISSAL_KEY = 'pwa-banner-dismissed-at';
  private readonly COOLDOWN_DAYS = 7;

  private platformId = inject(PLATFORM_ID);

  constructor() {}

  public initPwaPrompt() {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('beforeinstallprompt', (event: any) => {
        console.log('Install Event Dispatched!');
        event.preventDefault();
        this.promptEvent = event;
        this.promptAvailable$.next(true);
      });
    }
  }

  public getPromptAvailability(): Observable<boolean> {
    return this.promptAvailable$.asObservable();
  }

  public async triggerInstallPrompt(): Promise<boolean> {
    if (!this.promptEvent) {
      return false;
    }

    try {
      this.promptEvent.prompt();
      const choiceResult = await this.promptEvent.userChoice;

      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        this.promptEvent = null;
        this.promptAvailable$.next(false);
        return true;
      } else {
        console.log('User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  }

  private getDismissalKey(tenantSlug?: string): string {
    return tenantSlug && tenantSlug !== 'default'
      ? `${this.DISMISSAL_KEY}-${tenantSlug}`
      : this.DISMISSAL_KEY;
  }

  public shouldShowBanner(tenantSlug?: string): boolean {
    if (!this.promptEvent) {
      return false;
    }

    const key = this.getDismissalKey(tenantSlug);
    const dismissedAt = localStorage.getItem(key);
    if (!dismissedAt) {
      return true;
    }

    const dismissedTime = parseInt(dismissedAt, 10);
    if (isNaN(dismissedTime)) {
      // If corrupted value, remove it and show banner
      localStorage.removeItem(key);
      return true;
    }

    const currentTime = Date.now();
    const daysSinceDismissed =
      (currentTime - dismissedTime) / (1000 * 60 * 60 * 24);

    return daysSinceDismissed >= this.COOLDOWN_DAYS;
  }

  public markBannerDismissed(tenantSlug?: string): void {
    const key = this.getDismissalKey(tenantSlug);
    localStorage.setItem(key, Date.now().toString());
  }
}
